import React, { useEffect, useState, useCallback } from 'react';
import '@/App.css';
import { useAppStore } from '@/store';
import Home from '@/components/Home';
import MattersPage from '@/components/MattersPage';
import CoursesPage from '@/components/courses/CoursesPage';
import Settings from '@/components/settings/Settings';
import PromptAdmin from '@/components/admin/PromptAdmin';
import TopBar from '@/components/layout/TopBar';
import AppFooter from '@/components/layout/AppFooter';
import Stage0 from '@/components/stages/Stage0';
import Stage1 from '@/components/stages/Stage1';
import Stage2 from '@/components/stages/Stage2';
// Stage25 removed from navigation — the Strategy tab now lives inside
// Stage 2. Backend stage-id stays as 'stage_2_5' (no data change).
import Stage3 from '@/components/stages/Stage3';
import Stage4 from '@/components/stages/Stage4';
import Login from '@/components/auth/Login';
import DiagnosticPage from '@/components/admin/DiagnosticPage';
import UsersPage from '@/components/admin/UsersPage';
import SkillStudio from '@/components/admin/SkillStudio';
import StageJobsPage from '@/components/admin/StageJobsPage';
import DrawingPage from '@/components/drawings/DrawingPage';
import SpecPage from '@/components/spec/SpecPage';
import DescriptionWorkbench from '@/components/spec/DescriptionWorkbench';
import SpecIntakePage from '@/components/spec/SpecIntakePage';
// ARCH-F01 (Phase 24 3-A) — React error boundary. Wraps the three
// main workbench roots so an unhandled exception inside one view
// no longer crashes the whole app to a blank screen.
import { ErrorBoundary } from '@/components/ErrorBoundary';
import SpecSettings from '@/components/spec/SpecSettings';
import DrawingSettings from '@/components/drawings/DrawingSettings';
import { getMe, logout as apiLogout, setUnauthenticatedHandler, postEvent } from '@/api/client';

function MatterView({ onOpenSettings, onOpenPrompts, onOpenSkill, onOpenDiagnostic, onOpenUsers, onOpenStageJobs, onOpenMatters, onOpenCourses, onOpenDrawings, onLogout, user }) {
  const { activeStage } = useAppStore();

  return (
    <div data-testid="matter-view">
      <TopBar
        onOpenSettings={onOpenSettings}
        onOpenPrompts={onOpenPrompts}
        onOpenSkill={onOpenSkill}
        onOpenDiagnostic={onOpenDiagnostic}
        onOpenUsers={onOpenUsers}
        onOpenStageJobs={onOpenStageJobs}
        onOpenMatters={onOpenMatters}
        onOpenCourses={onOpenCourses}
        onLogout={onLogout}
        currentUser={user}
      />
      {/* Stage 0 — pre-analysis upload screen. New matters land here
          from POST /matter; existing stage_1+ matters skip it on
          open but the tab is still clickable (TopBar gates only on
          STAGE_ORDER index ≤ current_stage index, and stage_0 is
          index 0). */}
      {activeStage === 'stage_0' && <Stage0 />}
      {activeStage === 'stage_1' && <Stage1 />}
      {/* Stage 2 + Strategy tab share one screen. activeStage=stage_2_5
          lands on the Strategy tab inside Stage 2 — backend identifiers
          are unchanged; this is a pure UI merge. */}
      {(activeStage === 'stage_2' || activeStage === 'stage_2_5') && <Stage2 />}
      {activeStage === 'stage_3' && <Stage3 />}
      {activeStage === 'stage_4' && <Stage4 onOpenDrawings={onOpenDrawings} />}
      {/* Shared footer — copyright line + epi compliance disclosure.
          Mounted here so every workspace stage renders the disclosure;
          Home / admin pages keep their own AppFooter mounts (same
          component, same disclosure). */}
      <AppFooter />
    </div>
  );
}

function App() {
  const { matterId, matterState, setMatterId } = useAppStore();
  const [view, setView] = useState('matter');
  const [authState, setAuthState] = useState({ status: 'loading', user: null });
  // BUG-DRAW-010 — deferred deep-link target. The previous fix
  // (BUG-SPEC-008 Phase 5a) parsed the hash + immediately fired
  // setMatterId + setView on mount, but two race windows
  // existed:
  //   1. authState='loading' at mount → user sees "Loading…"
  //      → resolves to 'anonymous' → Login renders. After
  //      successful login, the original React `view` state is
  //      still 'spec'/'drawings' but no effect re-asserts the
  //      navigation if anything else (e.g. matterId in the
  //      zustand store) was reset between login and the next
  //      render. Result: drafter lands on Home.
  //   2. A malformed matterId in the hash (extra slashes,
  //      empty string) silently passed and produced a
  //      DrawingPage with a bogus id that 404'd in hydrate.
  // Holding the target in a `pendingDeepLink` slot until auth
  // resolves + matterId-format validation runs gives a
  // deterministic delivery: navigation happens iff the user
  // is authenticated AND the hash carries a UUID-shaped id.
  const [pendingDeepLink, setPendingDeepLink] = useState(null);
  // The page the Courses link was opened from, so Back returns there
  // (the link is on every page header now, not only the claims view).
  const [coursesFrom, setCoursesFrom] = useState('matter');

  // BUG-DRAW-010 — UUID v4 shape. We accept any
  // 8-4-4-4-12 hex pattern (matter ids in this codebase are
  // always v4 UUIDs; tightening to the specific version digit
  // would refuse legitimate ids written before the v4 default
  // landed). Case-insensitive so a clipboard-pasted ID with
  // uppercase hex characters still resolves.
  const _UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Mount: parse hash, validate, stash target. Clearing the
  // hash via history.replaceState prevents an inadvertent
  // re-trigger if the user navigates back home + then back via
  // the back button (hash would otherwise re-fire the deep-
  // link effect on every URL change that touched the hash).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash || '';
    if (!hash) return;
    const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
    if (parts.length < 2) return;
    const [rawTarget, rawId] = parts;
    const target = rawTarget === 'drawing' ? 'drawings' : rawTarget;
    if (target !== 'spec' && target !== 'drawings'
        && target !== 'description' && target !== 'spec-intake') return;
    if (!_UUID_RE.test(rawId)) {
      // Malformed id — refuse silently. Don't strand the user
      // on Home with a confusing toast; the URL was likely
      // hand-typed.
      return;
    }
    setPendingDeepLink({ view: target, matterId: rawId });
    // Clear the hash so subsequent navigations don't re-trigger.
    // The view-sync effect below re-writes the hash from current
    // state whenever the drafter lands on spec/drawings.
    try {
      window.history.replaceState(
        null, '', window.location.pathname + window.location.search,
      );
    } catch (_e) { /* private-mode browsers throw; non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the pending deep-link once auth has resolved AND the
  // user is authenticated. If the user is anonymous the
  // navigation waits in the pending slot through the Login
  // round-trip; after refreshAuth flips to 'authenticated' this
  // effect re-runs and lands the deep-link cleanly.
  useEffect(() => {
    if (!pendingDeepLink) return;
    if (authState.status !== 'authenticated') return;
    setMatterId(pendingDeepLink.matterId);
    setView(pendingDeepLink.view);
    setPendingDeepLink(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeepLink, authState.status]);

  // BUG-DRAW-010 — share-URL sync. When the drafter is on the
  // spec or drawing view, mirror their state into the URL hash
  // so bookmarking + share-URL flows work. On every other view
  // (matter / settings / prompts / etc.) clear the hash so the
  // URL bar reflects "this is not a drawing/spec context".
  // ``replaceState`` (not pushState) avoids polluting browser
  // history with synthetic entries the back button would
  // otherwise step through.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if ((view === 'drawings' || view === 'spec'
         || view === 'description' || view === 'spec-intake') && matterId) {
      const targetHash = `#/${view}/${matterId}`;
      if (window.location.hash !== targetHash) {
        try {
          window.history.replaceState(
            null, '',
            window.location.pathname + window.location.search + targetHash,
          );
        } catch (_e) { /* non-fatal */ }
      }
    } else if (window.location.hash) {
      // Non-deep-link view; strip the hash so the URL bar
      // doesn't carry a stale anchor.
      try {
        window.history.replaceState(
          null, '', window.location.pathname + window.location.search,
        );
      } catch (_e) { /* non-fatal */ }
    }
  }, [view, matterId]);

  const refreshAuth = useCallback(async () => {
    try {
      const me = await getMe();
      if (me.authenticated) {
        setAuthState({ status: 'authenticated', user: me.user });
      } else {
        setAuthState({ status: 'anonymous', user: null });
      }
    } catch {
      setAuthState({ status: 'anonymous', user: null });
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    // Any 401 from the API bounces to the login screen — handles
    // mid-session expiry and admin-initiated deactivation cleanly.
    setUnauthenticatedHandler(() => {
      setAuthState({ status: 'anonymous', user: null });
    });
  }, []);

  // page_view beacon — fires every time the top-level view changes
  // UX-F03 (Phase 28 3-H) — bounce non-admin users away from
  // admin-gated views + bounce drawing/spec away when matterId
  // is unset. Was previously a series of inline
  // ``setView('matter')`` calls inside the render conditional
  // (lines ~178/185/192/201/209/224/245/269). Calling setState
  // during render is a React anti-pattern: it causes an
  // immediate second render (a one-frame null flash) and will
  // throw under React 18 strict mode + Concurrent Mode. Doing
  // it inside an effect lets React commit the bounce cleanly.
  //
  // The render-side guards now return null without firing
  // setView — this effect handles the redirect on the next
  // commit cycle. Net behaviour identical for the drafter; no
  // more strict-mode warning + no more wasted render pass.
  useEffect(() => {
    const isAdmin = authState.user?.role === 'admin';
    const adminGated = new Set([
      'settings', 'prompts', 'skill', 'diagnostic',
      'users', 'stage-jobs',
    ]);
    if (adminGated.has(view) && !isAdmin) {
      setView('matter');
      return;
    }
    if ((view === 'drawings' || view === 'spec'
         || view === 'description' || view === 'spec-intake') && !matterId) {
      setView('matter');
    }
  }, [view, matterId, authState.user?.role]);

  // while the user is authenticated. Covers matter / matters list /
  // settings / prompts / diagnostic / users. Gated on authenticated
  // so the login screen doesn't produce spurious events, and on the
  // initial load it still fires once we flip to 'authenticated'.
  useEffect(() => {
    if (authState.status !== 'authenticated') return;
    const pageMap = {
      matter: '/matter',
      matters: '/matters',
      courses: '/courses',
      settings: '/settings',
      prompts: '/admin/prompts',
      diagnostic: '/admin/diagnostic',
      users: '/admin/users',
      'stage-jobs': '/admin/stage-jobs',
    };
    postEvent({
      event_type: 'page_view',
      event_category: 'navigation',
      element_id: `view_${view}`,
      event_data: { view, path: pageMap[view] || `/${view}` },
    });
  }, [view, authState.status]);

  const openCourses = useCallback(() => {
    setCoursesFrom(view === 'courses' ? 'matter' : view);
    setView('courses');
  }, [view]);

  const handleLogout = useCallback(async () => {
    try { await apiLogout(); } catch { /* best-effort */ }
    // Reset in-memory matter state so another user doesn't see prior data.
    useAppStore.getState().reset?.();
    setAuthState({ status: 'anonymous', user: null });
  }, []);

  if (authState.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (authState.status === 'anonymous') {
    return <Login onAuthenticated={refreshAuth} />;
  }

  // BUG-DRAW-010 — bridge state while the deep-link is being
  // applied. Between the moment auth flips to 'authenticated'
  // and the moment the deep-link effect re-runs, view is still
  // 'matter' and the render branches below would flash Home
  // for one frame. A short "Loading matter…" placeholder
  // smooths over the gap. Pure visual nicety — once the effect
  // commits, this branch falls through and the matched
  // drawings/spec route below renders.
  if (pendingDeepLink) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-sm text-muted-foreground"
        data-testid="app-deep-link-pending"
      >
        Loading matter…
      </div>
    );
  }

  // Settings, Prompts, Users, Diagnostic are all admin-only. Server
  // enforces via require_admin; the client-side bounce is a UX nicety
  // so a drafter who types the URL doesn't see a 403 shell.
  if (view === 'settings') {
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <Settings onBack={() => setView('matter')} />;
  }
  if (view === 'prompts') {
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <PromptAdmin onBack={() => setView('matter')} />;
  }
  if (view === 'skill') {
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <SkillStudio onBack={() => setView('matter')} />;
  }
  if (view === 'diagnostic') {
    // /admin/diagnostic — admin-only inspector. Server enforces the
    // role check; this client-side guard is just a UX nicety.
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <DiagnosticPage onBack={() => setView('matter')} />;
  }
  if (view === 'users') {
    // /admin/users — same admin gate as diagnostic.
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return (
      <UsersPage
        currentUser={authState.user}
        onBack={() => setView('matter')}
      />
    );
  }
  if (view === 'stage-jobs') {
    // /admin/stage-jobs — cross-module async-job observability.
    // Server-side require_admin enforces; client-side bounce
    // is the same UX nicety as the other admin views.
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <StageJobsPage onBack={() => setView('matter')} />;
  }
  if (view === 'matters') {
    // /matters — available to every authenticated user. Leaving the
    // current matter (if any) intact so "Back" returns to the workflow.
    return (
      <MattersPage
        onBack={() => setView('matter')}
        onOpenDrawings={() => setView('drawings')}
        onOpenSpecIntake={(id) => { setMatterId(id); setView('spec-intake'); }}
        onOpenCourses={openCourses}
      />
    );
  }
  if (view === 'courses') {
    // /courses — the user-guide videos, open to every authenticated
    // user like the matters list. "Back" returns to the workflow.
    return <CoursesPage onBack={() => setView(coursesFrom)} userId={authState.user?.id} />;
  }
  if (view === 'drawings') {
    // Drawing workbench (Phase 4) — opens after claims are frozen.
    // Server-side lock check is the source of truth; if matterId is
    // missing we bounce back. Same state-based pattern as
    // settings / prompts / matters — no react-router.
    if (!matterId) {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return (
      <ErrorBoundary label="DrawingPage">
        <DrawingPage
          matterId={matterId}
          onBack={() => setView('matter')}
          onOpenSettings={
            authState.user?.role === 'admin'
              ? () => setView('drawings-settings')
              : undefined
          }
          onOpenSpec={() => setView('spec')}
          onOpenCourses={openCourses}
        />
      </ErrorBoundary>
    );
  }
  if (view === 'spec') {
    // Spec workbench (Phase 4) — opens after at least one drawing is
    // frozen at Stage 5. Server-side lock check is the source of
    // truth; SpecPage surfaces the locked state with a CTA back to
    // drawings.
    if (!matterId) {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return (
      <ErrorBoundary label="SpecPage">
        <SpecPage
          matterId={matterId}
          userRole={authState.user?.role || null}
          onBack={() => setView('matter')}
          onOpenDrawings={() => setView('drawings')}
          onOpenDescription={() => setView('description')}
          onOpenCourses={openCourses}
          onOpenSettings={
            authState.user?.role === 'admin'
              ? () => setView('spec-settings')
              : undefined
          }
        />
      </ErrorBoundary>
    );
  }
  if (view === 'spec-intake') {
    // Specification-draft intake — the second road in, for a matter
    // whose claims and drawings already exist. It produces the same
    // rows the six-stage road produces and then hands over to the
    // Spec page, which is unchanged.
    if (!matterId) {
      return null;
    }
    return (
      <ErrorBoundary label="SpecIntakePage">
        <SpecIntakePage
          matterId={matterId}
          onBack={() => setView('matters')}
          onOpenSpec={() => setView('spec')}
          onOpenDrawings={() => setView('drawings')}
          onOpenCourses={openCourses}
        />
      </ErrorBoundary>
    );
  }
  if (view === 'description') {
    // DD workbench (DETAILED_DESCRIPTION_WORKBENCH_PLAN.md DD-4) —
    // Dhruva's claim-by-claim Detailed Description enablement page.
    // Same gating as spec: requires a matter; the backend enforces
    // frozen claims + the spec lock.
    if (!matterId) {
      return null;
    }
    return (
      <ErrorBoundary label="DescriptionWorkbench">
        <DescriptionWorkbench
          matterId={matterId}
          onBack={() => setView('spec')}
          onOpenCourses={openCourses}
        />
      </ErrorBoundary>
    );
  }
  if (view === 'spec-settings') {
    // Admin-only spec settings page (Phase 4 Slice 4F). require_admin
    // on the server is the source of truth; the client-side gate
    // inside SpecSettings is a UX nicety that surfaces a 403 panel
    // for drafters who land here directly.
    return (
      <SpecSettings
        userRole={authState.user?.role}
        onBack={() => setView(matterId ? 'spec' : 'matter')}
        onOpenPrompts={
          authState.user?.role === 'admin'
            ? () => setView('prompts')
            : undefined
        }
      />
    );
  }
  if (view === 'drawings-settings') {
    // Admin-only drawing settings (Phase 4F). Server-side require_admin
    // is the source of truth; the client-side gate below is a UX nicety
    // so a drafter who lands here directly sees a clean 403 panel
    // instead of a wall of red.
    return (
      <DrawingSettings
        userRole={authState.user?.role}
        onBack={() => setView(matterId ? 'drawings' : 'matter')}
        onOpenPrompts={
          authState.user?.role === 'admin'
            ? () => setView('prompts')
            : undefined
        }
      />
    );
  }

  const isAdmin = authState.user?.role === 'admin';
  // Every admin entry point is gated on isAdmin so non-admin users
  // never see the nav button — not greyed out, not CSS-hidden, the
  // prop is undefined and TopBar/Home render guards skip the element
  // entirely (absent from the DOM).
  const onOpenSettings = isAdmin ? () => setView('settings') : undefined;
  const onOpenPrompts = isAdmin ? () => setView('prompts') : undefined;
  const onOpenSkill = isAdmin ? () => setView('skill') : undefined;
  const onOpenDiagnostic = isAdmin ? () => setView('diagnostic') : undefined;
  const onOpenUsers = isAdmin ? () => setView('users') : undefined;
  const onOpenStageJobs = isAdmin ? () => setView('stage-jobs') : undefined;
  // Matters list is available to every authenticated user — not
  // admin-gated. Server still scopes results by workspace.
  const onOpenMatters = () => setView('matters');
  // So are the user-guide videos.
  const onOpenCourses = openCourses;

  if (matterId && matterState) {
    return (
      <ErrorBoundary label="MatterView">
        <MatterView
          onOpenSettings={onOpenSettings}
          onOpenPrompts={onOpenPrompts}
          onOpenSkill={onOpenSkill}
          onOpenDiagnostic={onOpenDiagnostic}
          onOpenUsers={onOpenUsers}
          onOpenStageJobs={onOpenStageJobs}
          onOpenMatters={onOpenMatters}
          onOpenCourses={onOpenCourses}
          onOpenDrawings={() => setView('drawings')}
          onLogout={handleLogout}
          user={authState.user}
        />
      </ErrorBoundary>
    );
  }

  return (
    <Home
      onOpenSettings={onOpenSettings}
      onOpenPrompts={onOpenPrompts}
      onOpenSkill={onOpenSkill}
      onOpenDiagnostic={onOpenDiagnostic}
      onOpenUsers={onOpenUsers}
      onOpenStageJobs={onOpenStageJobs}
      onOpenMatters={onOpenMatters}
      onOpenCourses={onOpenCourses}
      onOpenSpec={() => setView('spec')}
      onOpenDrawings={() => setView('drawings')}
      onOpenSpecIntake={(id) => { setMatterId(id); setView('spec-intake'); }}
      onLogout={handleLogout}
      currentUser={authState.user}
    />
  );
}

export default App;
